import React from 'react';

import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { IPendingCart } from 'models/cart/IPendingCart';
import { ICreatePendingCart } from 'modules/create-pending-cart';
import { completeCheckout } from 'modules/complete-checkout';
import { clientIp, individualState } from 'state/individual/individual-state';
import { PaymentErrorModal } from 'components/modals/PaymentErrorModal/PaymentErrorModal';
import { Spinner } from 'components/shared/Spinner/Spinner';

import './style.scss';

interface IFatZebraProps {
    handlePaymentSuccess: () => void;
    pendingCart: IPendingCart;
    queryString?: ICreatePendingCart;
    completeToken: string;
    loadRecaptcha: () => void;
    setPaymentState: React.Dispatch<React.SetStateAction<boolean>>;
    paymentState: boolean;
}

export const FatZebraForm = ({
    handlePaymentSuccess,
    pendingCart,
    queryString,
    completeToken,
    loadRecaptcha,
    setPaymentState,
    paymentState
}: IFatZebraProps) => {
    const {t} = useTranslation();

    const [checkoutInProgress, setCheckoutInProgress] = React.useState<any | false>(false);
    const [errorModal, setErrorModal] = React.useState<boolean | string>(false);
    const [errorMessage, setErrorMessage] = React.useState<string[] | undefined>();
    const clientIP = useRecoilValue(clientIp);

    const newWorker = useRecoilValue(individualState);

    React.useEffect(() => {
        if (checkoutInProgress && completeToken) {
            completeCheckout({
                portalId: queryString?.portalId || '',
                companyId: queryString?.companyId || '',
                employeeId: queryString?.employeeId || '',
                cartId: pendingCart?.cart?.id || 0,
                token: completeToken,
                paymentId: pendingCart?.createPayment?.paymentId || '',
                newWorker,
                fatZebraToken: checkoutInProgress.token,
                validation: checkoutInProgress.v,
                ip: clientIP || ''
            }).then((res) => {
                if ('errorCode' in res && res.errorCode) {
                    setErrorModal(res.errorCode);
                } else {
                    handlePaymentSuccess();
                }
                setCheckoutInProgress(false);
            });
        }
    }, [
        checkoutInProgress,
        completeToken,
        newWorker,
        queryString,
        pendingCart,
        handlePaymentSuccess,
        clientIP,
        setCheckoutInProgress,
    ]);

    /**
     * Checks if an element is an iFrame
     * @param input
     */
    const isIFrame = (input: HTMLElement | null): input is HTMLIFrameElement =>
        input !== null && input.tagName === 'IFRAME';

    React.useEffect(() => {
        window.paymentHost = pendingCart?.createPayment?.host || '';

        const messageListener = function (event: any) {
            if (!event.origin.includes(window.paymentHost)) {
                console.log('The origin does not match', event);
                return;
            }

            // Older browsers will have a query-string style data payload
            // Whereas newer browsers will have an object
            let payload = event.data;
            if (typeof event.data == 'string') {
                if (/\[object/i.test(event.data)) {
                    alert('Sorry, it looks like there has been a problem communicating with your browser...'); // Raised if the serialization failed
                }
                // Deserialize into an object
                const pairs = payload.split('&');
                payload = {};
                for (let i = 0; i < pairs.length; i++) {
                    const element = pairs[i];
                    const kv = element.split('=');
                    payload[kv[0]] = kv[1];
                }
            }

            if ('data' in payload) {
                payload = payload.data;
            }
            switch (event.data.message || payload.message) {
                case 'transaction.complete':
                    if (!checkoutInProgress) {
                        setCheckoutInProgress(payload);
                        // Handle the transaction complete message.
                    }
                    return true;
                case 'transaction.processing':
                    // Handle the processing of the transaction - implementation optional.
                    return true;
                case 'transaction.cancelled':
                    // Handle the transaction being cancelled (i.e. show message, re-display the window etc).
                    return true;
                case 'form.invalid':
                    const messages = payload.errors.split(', ');
                    setErrorMessage(messages);
                    return true;
            }
        };

        if (window.addEventListener) {
            window.addEventListener('message', messageListener);
        } else {
            window.attachEvent('onmessage', messageListener);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Changes processing state and submits data to Fat Zebra
     */
    const handleSubmitPayment = () => {
        loadRecaptcha();
        setPaymentState(true);

        const frame = document.getElementById('iframe');
        if (isIFrame(frame) && frame.contentWindow) {
            frame.contentWindow.postMessage('doCheckout', '*');
        }
    };

    /**
     * Closes the error modal, refreshes the Fat Zebra form
     */
    const handleCloseError = () => {
        const frame = document.getElementById('iframe');

        if (isIFrame(frame) && frame.contentWindow) {
            // This is needed to trigger a reload of the Fat Zebra form
            // eslint-disable-next-line no-self-assign
            frame.src = frame.src;
        }
        // shows payment form again
        setPaymentState(false);
        // iframe has an unsightly delay when being shown, this hides it
        setTimeout(() => {
            setErrorModal(false);
        }, 500);
    };

    return (
        <>
            <PaymentErrorModal handleClose={handleCloseError} show={!!errorModal} error={errorModal}/>
            <div className="CreditPayment__buttons-div">
                {errorMessage && !paymentState && errorMessage.map((message, index) => {
                    return (
                        <li className="CreditPayment__errorMessage" key={index}>
                            {message}
                        </li>
                    );
                })}

                <div id="CreditPayment__checkoutIframe" style={{height: paymentState ? 0 : '18rem'}}>
                    <iframe
                        id="iframe"
                        allow="payment"
                        width="100%"
                        src={pendingCart?.createPayment?.approvalUrl} title="FatZebra Checkout Form"
                    />
                </div>
                <Button
                    id="CreditPayment__checkout-button"
                    type="submit"
                    variant="primary"
                    className="btn-xl"
                    block
                    onClick={() => handleSubmitPayment()}
                    disabled={paymentState}
                >
                    {!paymentState ? (
                        t('payment:actions.payNow', 'Pay Now')
                    ) : (
                        <>
                            {t('payment:actions.payNow', 'Processing Payment...', {context: 'busy'})}
                            <Spinner variant="primary" size="sm" className="ml-2 mr-0"/>
                        </>
                    )}
                </Button>
            </div>
        </>
    );
};
